import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaArrowDown, FaArrowUp, FaClock, FaCalendarAlt } from 'react-icons/fa';
import { AiOutlineClose } from 'react-icons/ai';
import { FiDownload, FiSearch } from 'react-icons/fi';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Styled Components
const DashboardWrapper = styled.div`
  padding: 20px 40px;
`;

const PageHeading = styled.h2`
  font-size: 25px;
  color: #2B3497;
  margin-bottom: 25px;
`;

const PageHeadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background-color: ${(props) => props.bgColor || '#fff'};
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  color: #000;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    color: #fff;
    .icon {
      color: #fff;
    }
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .icon {
    font-size: 20px;
    color: #000;
  }

  .value {
    font-size: 28px;
    font-weight: bold;
    margin: 10px 0 5px;
  }

  p {
    font-size: 14px;
    margin: 0;
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
  }
`;

const TableContainer = styled.div`
  margin-top: 30px;
  width: 100%;
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  background: linear-gradient(to right, #e6e6e6, #f2f2f2, #f5f5f5, #f3f3f3, #e9e9e9);
  box-shadow: 0px 10px 20px #0000001a;
  border-radius: 10px;
`;

const TableHeader = styled.th`
  background-color: #e6e6e6;
  color: #000;
  padding: 9px 10px;
  text-align: left;
  font-size: 14px;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: #f5f5f5;
  }

  &:hover {
    background-color: #d9d9d9;
  }
`;

const TableCell = styled.td`
  padding: 10px;
  text-align: left;
  font-size: 13px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  gap: 10px;
`;

const PaginationButton = styled.button`
  background-color: #3d46b4;
  color: #fff;
  border: none;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 5px;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #293495;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const DropdownSelect = styled.select`
  background-color: #fff;
  color: #293495;
  border: 1px solid #3d46b4;
  padding: 8px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: #293495;
  }
`;

const Button = styled.button`
  width: 51px;
  height: 32px;
  border-radius: 7px;
  box-shadow: 0px 5px 10px #2b349754;
  color: white;
  font-size: 14px;
  border: none;
  cursor: pointer;
  font-family: 'Segoe UI', Semibold;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #2b3497;
`;
const SearchBox = styled.div`
  display: flex;
  align-items: center;
  box-shadow: 0px 3px 10px #00000029;
  border-radius: 8px;
  padding: 7px;
  width: 248px;
  background: white;
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  font-size: 16px;
  padding: 5px;
  flex: 1;
`;

const SearchIcon = styled(FiSearch)`
  margin-right: 10px;
  color: #000;
`;

const DropdownContainerTwoo = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const ExportIcono = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  margin-left: 10px;
  color: #293495;
`;

const OptionsPanelo = styled.div`
  position: absolute;
  top: 30px;
  right: 0;
  background-color: #f9f9f9;
  border: 1px solid #d3d3d3;
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  width: 150px;
  z-index: 10;
`;

const OptionItemo = styled.div`
  padding: 10px;
  cursor: pointer;
  text-align: left;
  &:hover {
    background-color: #f1f1f1;
  }
`;


const DashboardMaterial = () => {
  const [selectedCard, setSelectedCard] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 7; // or itemsPerPage
  const [searchTerm, setSearchTerm] = useState('');

  const dummyData = [
    {
      transactionId: 'TXN001',
      materialName: 'Steel Rod',
      materialUid: 'MAT123456',
      description: '12mm Steel Rod',
      quantity: 50,
      unit: 'Kg',
      returnRatio: '100%',
      status: 'Overdue',
      dueDate: '2025-07-25',
    },
    {
      transactionId: 'TXN002',
      materialName: 'Cement Bags',
      materialUid: 'MAT654321',
      description: '50kg Grade A',
      quantity: 20,
      unit: 'Bags',
      returnRatio: '80%',
      status: 'Upcoming',
      dueDate: '2025-07-30',
    },
    {
      transactionId: 'TXN003',
      materialName: 'Aluminium Sheet',
      materialUid: 'MAT789123',
      description: '8x4 ft, 2mm',
      quantity: 10,
      unit: 'Sheets',
      returnRatio: '50%',
      status: 'Overdue',
      dueDate: '2025-07-20',
    },
  ];
  const [searchQuery, setSearchQuery] = useState('');
  const [isPanelOpenE, setIsPanelOpenE] = useState(false);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const togglePanelE = () => {
    setIsPanelOpenE(!isPanelOpenE);
  };

  const handleExportOptionClick = (format) => {
    if (format === 'excel') {
      exportToExcel(); // Call the Excel export function
    } else if (format === 'pdf') {
      exportToPDF(); // Call the PDF export function
    }

    // Close the panel after making a selection
    setIsPanelOpenE(false);
  };





  const [dashboardCounts, setDashboardCounts] = useState({
    inwardMaterialCount: 0,
    outwardMaterialCount: 0,
    overdueMaterialCount: 0,
    upcomingReturnCount: 0,
  });


  useEffect(() => {
    const fetchDashboardCounts = async () => {
      try {
        const response = await axios.get('MaterialTransaction/GetDashboardCounts');
        setDashboardCounts(response.data);
      } catch (error) {
        console.error('Failed to fetch dashboard counts', error);
      }
    };

    fetchDashboardCounts();
  }, []);

  const handleCardClick = async (cardType) => {
    setSelectedCard(cardType);
    const type = cardType === 'overdue' ? 3 : 4;


    // try {
    //   const response = await axios.get(
    //     `MaterialTransaction/GetDashboardDetails?dashboard=${type}`
    //   );
    //   setTableData(response.data || []);
    //   console.log("response", response);

    //   setCurrentPage(1); // reset to page 1 on card change
    // } catch (error) {
    //   console.error("Failed to fetch table data", error);
    // }
    if (cardType === 'overdue') {
      try {
        const response = await axios.get('MaterialTransaction/GetOverdueMaterials');
        setTableData(response.data || []);
        console.log("response", response);

        setCurrentPage(1); // reset to page 1 on card change
      } catch (error) {
        console.error("Failed to fetch table data", error);
      }

    } else {
      try {
        const response = await axios.get('MaterialTransaction/GetReturnableMaterials');
        setTableData(response.data || []);
        console.log("response", response);

        setCurrentPage(1); // reset to page 1 on card change
      } catch (error) {
        console.error("Failed to fetch table data", error);
      }
    }
  };

const filteredData = tableData.filter((txn) => {
  const search = (searchTerm || '').toLowerCase().trim();

  // Match top-level transaction ID
  const transactionMatch = txn.transactionId?.toLowerCase().includes(search);

  // Match materials array
  const materialsMatch = txn.materials?.some((material) => {
    const dueDateFormatted = material.dueDate
      ? new Date(material.dueDate).toLocaleString('en-CA', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }).toLowerCase()
      : '';

    const status = material.isPartiallyReturned
      ? 'partially returned'
      : material.returnedQuantity === material.quantity
        ? 'returned'
        : 'pending';

    return (
      material.materialUid?.toLowerCase().includes(search) ||
      material.name?.toLowerCase().includes(search) ||
      material.description?.toLowerCase().includes(search) ||
      String(material.quantity).toLowerCase().includes(search) ||
      material.unit?.toLowerCase().includes(search) ||
      material.returnRatio?.toLowerCase().includes(search) ||
      dueDateFormatted.includes(search) ||
      status.toLowerCase().includes(search)
    );
  });

  return transactionMatch || materialsMatch;
});




  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);


  const handlePageChange = (event) => {
    setCurrentPage(Number(event.target.value));
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(prev => prev - 1);
  };

  const exportToExcel = () => {
    const flattenedExportData = filteredData.flatMap((row) =>
      row.materials.map((material) => ({
        'Transaction ID': row.transactionId,
        'Material Name': material.name,
        'Material UID': material.materialUid,
        'Description': material.description,
        'Quantity': material.quantity,
        'Unit': material.unit,
        'Return Ratio': material.returnRatio,
        'Status': material.isPartiallyReturned
          ? 'Partially Returned'
          : material.returnedQuantity === material.quantity
            ? 'Returned'
            : 'Pending',
        'Due Date': new Date(material.dueDate).toLocaleString('en-CA', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }),
      }))
    );

    const worksheet = XLSX.utils.json_to_sheet(flattenedExportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Material Reports');

    worksheet['!cols'] = [
      { wpx: 120 },
      { wpx: 150 },
      { wpx: 150 },
      { wpx: 200 },
      { wpx: 100 },
      { wpx: 100 },
      { wpx: 130 },
      { wpx: 120 },
      { wpx: 150 },
    ];

    XLSX.writeFile(workbook, 'Material_Report.xlsx');
  };

  const exportToPDF = () => {
    const doc = new jsPDF(); // Portrait by default
    doc.setFontSize(14);
    doc.text('Material Reports', 14, 16);

    const bodyData = filteredData.flatMap((row) =>
      row.materials.map((material) => [
        row.transactionId,
        material.name,
        material.materialUid,
        material.description,
        material.quantity,
        material.unit,
        material.returnRatio,
        material.isPartiallyReturned
          ? 'Partially Returned'
          : material.returnedQuantity === material.quantity
            ? 'Returned'
            : 'Pending',
        new Date(material.dueDate).toLocaleString('en-CA', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }),
      ])
    );

    doc.autoTable({
      head: [[
        'Transaction ID',
        'Material Name',
        'Material UID',
        'Description',
        'Quantity',
        'Unit',
        'Return Ratio',
        'Status',
        'Due Date',
      ]],
      body: bodyData,
      startY: 30,
      theme: 'grid',
      styles: {
        fontSize: 8,
      },
      // headStyles: {
      //   fillColor: [41, 52, 149], // #293495
      //   textColor: [255, 255, 255], // White text
      // },
    });

    doc.save('Material_Report.pdf');
  };





  return (
    <DashboardWrapper>
      <PageHeading>Dashboard</PageHeading>

      <CardGrid>
        <Card bgColor="#A2BECB">
          <div className="card-header">
            <h3>Inward Materials</h3>
            <div className="icon"><FaArrowDown /></div>
          </div>
          <div className="value">{dashboardCounts.inwardMaterialCount}</div>
          <p>Total inward materials</p>
        </Card>

        <Card bgColor="#FFCC66">
          <div className="card-header">
            <h3>Outward Materials</h3>
            <div className="icon"><FaArrowUp /></div>
          </div>
          <div className="value">{dashboardCounts.outwardMaterialCount}</div>
          <p>Total outward materials</p>
        </Card>

        <Card bgColor="#FF6B6B" onClick={() => handleCardClick("overdue")}>
          <div className="card-header">
            <h3>Overdue Materials</h3>
            <div className="icon"><FaClock /></div>
          </div>
          <div className="value">{dashboardCounts.overdueMaterialCount}</div>
          <p>Click to toggle details</p>
        </Card>

        <Card bgColor="#FFD54F" onClick={() => handleCardClick("upcoming")}>
          <div className="card-header">
            <h3>Upcoming Returns</h3>
            <div className="icon"><FaCalendarAlt /></div>
          </div>
          <div className="value">{dashboardCounts.upcomingReturnCount}</div>
          <p>Due in next 10 days</p>
        </Card>
      </CardGrid>


      {selectedCard && (
        <TableContainer>
          <PageHeadingWrapper>
            <PageHeading>
              {selectedCard === "overdue"
                ? "Overdue Materials List"
                : "Upcoming Returns List"}
            </PageHeading>

            <div style={{ display: 'flex', alignItems: 'center', position: 'absolute', right: '8%' }}>
              <SearchBox>
                <SearchIcon size={20} />
                <SearchInput
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />

              </SearchBox>
              <DropdownContainerTwoo>
                <ExportIcono onClick={togglePanelE} title="Export">
                  <FiDownload size={25} />
                </ExportIcono>
                {isPanelOpenE && (
                  <OptionsPanelo>
                    <OptionItemo onClick={() => handleExportOptionClick('excel')}>Excel</OptionItemo>
                    <OptionItemo onClick={() => handleExportOptionClick('pdf')}>PDF</OptionItemo>
                  </OptionsPanelo>
                )}
              </DropdownContainerTwoo>
            </div>

            <Button onClick={() => setSelectedCard(null)}>
              <AiOutlineClose />
            </Button>
          </PageHeadingWrapper>


          {currentRows.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', fontSize: '18px', color: '#999' }}>
              No materials to display
            </div>
          ) : (
            <>
              <StyledTable>
                <thead>
                  <tr>
                    <TableHeader>Transaction ID</TableHeader>
                    <TableHeader>Material Name</TableHeader>
                    <TableHeader>Material UID</TableHeader>
                    <TableHeader>Description</TableHeader>
                    <TableHeader>Quantity</TableHeader>
                    <TableHeader>Unit</TableHeader>
                    <TableHeader>Return Ratio</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Due Date</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {currentRows.map((txn, txnIndex) =>
                    txn.materials.map((item, itemIndex) => (
                      <TableRow key={`${txn.transactionId}-${itemIndex}`}>
                        <TableCell>{txn.transactionId}</TableCell> {/* Always show Transaction ID */}
                        <TableCell>{item.name}</TableCell>
                        <TableCell>{item.materialUid}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>{item.unit}</TableCell>
                        <TableCell>{item.returnRatio}</TableCell>
                        <TableCell>
                          {item.isPartiallyReturned
                            ? "Partially Returned"
                            : item.returnedQuantity === item.quantity
                              ? "Returned"
                              : "Pending"}
                        </TableCell>
                        <TableCell>
                          {new Intl.DateTimeFormat("en-CA", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                            hour12: false
                          })
                            .format(new Date(item.dueDate))
                            .replace(",", " :")}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </tbody>
              </StyledTable>

              <PaginationContainer>
                <PaginationButton onClick={handlePrevPage} disabled={currentPage === 1}>
                  Previous
                </PaginationButton>

                <DropdownSelect value={currentPage} onChange={handlePageChange}>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      Page {i + 1}
                    </option>
                  ))}
                </DropdownSelect>

                <PaginationButton onClick={handleNextPage} disabled={currentPage === totalPages}>
                  Next
                </PaginationButton>
              </PaginationContainer>
            </>
          )}


        </TableContainer>
      )}
    </DashboardWrapper>
  );
};

export default DashboardMaterial;
